import { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Form, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";

/**
 * The user's personal default title CSS — the bottom of the chain
 * personal default -> group default -> the image's own CSS.
 *
 * A post uses its author's personal default, so this styles the title on every
 * image in your posts, unless the post is shared into a group with its own
 * default or the image has custom CSS.
 */
const PersonalOverlayCss = () => {
  const { t } = useTranslation();
  const [css, setCss] = useState("");
  const [builtInCss, setBuiltInCss] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    crudApi.justGet(SocialEndpoints.settings.overlayCss).then((response) => {
      if (cancelled || !response?.ok) return;
      setCss(response.data?.css || "");
      setBuiltInCss(response.data?.builtInCss || "");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    await crudApi.edit(SocialEndpoints.settings.overlayCss, {
      css: css.trim() === "" ? null : css,
    });
    setSaving(false);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-title mb-0">
          {t("Backend.Settings.OverlayCss.Title", "Default title CSS")}
        </h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted small">
          {t(
            "Backend.Settings.OverlayCss.Hint",
            "Used for the title on every image in your posts, unless the post is shared into a group with its own default or the image has custom CSS. Leave empty to use the built-in style."
          )}
        </p>
        <Form.Control
          as="textarea"
          rows={12}
          spellCheck={false}
          value={css}
          placeholder={builtInCss}
          onChange={(event) => setCss(event.target.value)}
          style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
        />
        <div className="d-flex gap-2 mt-2">
          <Button onClick={save} disabled={saving}>
            {t("Backend.Settings.OverlayCss.Save", "Save default CSS")}
          </Button>
          {css.trim() !== "" && (
            <Button variant="link" onClick={() => setCss("")}>
              {t("Backend.Settings.OverlayCss.Clear", "Clear (use built-in style)")}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default PersonalOverlayCss;
